/**
 * Resolves the player's currency code from balances.
 * Matches the player's currency_id against the balances array
 * and returns the corresponding code.
 *
 * @param {string} currencyId - The player's currency_id
 * @param {Array} balances - Player balances array (each with currency_id and code)
 * @returns {string|undefined} The currency code or undefined if no match
 */
export function resolveCurrencyCode(currencyId, balances) {
  if (!currencyId || !Array.isArray(balances)) return undefined
  const match = balances.find((balance) => balance?.currency_id === currencyId)
  return match?.alphabetic_code
}

/**
 * Filters the lockers object to only those with allowed: false.
 *
 * @param {Object} lockers - The lockers object from the API response
 * @returns {Array<{ name: string, reason: string }>} Array of blocking lockers
 */
export function getBlockingLockers(lockers) {
  if (!lockers || typeof lockers !== 'object') return []
  return Object.entries(lockers)
    .filter(([, value]) => value?.allowed === false)
    .map(([name, value]) => ({ name, reason: value?.reason }))
}

/**
 * Formats a key by replacing underscores with spaces and capitalizing each word.
 * Used for locker names (e.g. "network_status" → "Network Status").
 *
 * @param {string} key - The raw key (e.g. "network_status")
 * @returns {string} Formatted label (e.g. "Network Status")
 */
export function formatLockerName(key) {
  if (!key || typeof key !== 'string') return ''
  return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

/**
 * Formats blocking lockers for tooltip display.
 * Each locker is formatted as "{FormattedName}: {reason}" separated by newlines.
 *
 * @param {Array<{ name: string, reason: string }>} blockingLockers
 * @returns {string} Formatted string of blocking lockers
 */
export function formatBlockingLockers(blockingLockers) {
  if (!Array.isArray(blockingLockers)) return ''
  return blockingLockers
    .map((locker) => `${formatLockerName(locker.name)}: ${locker.reason}`)
    .join('\n')
}
